import { Component, Inject, OnDestroy, OnInit } from '@angular/core';
import { CommonModule } from '@angular/common';
import { MAT_DIALOG_DATA, MatDialogModule, MatDialogRef } from '@angular/material/dialog';
import { MatButtonModule } from '@angular/material/button';
import { MatIconModule } from '@angular/material/icon';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { MatTooltipModule } from '@angular/material/tooltip';
import { Subject } from 'rxjs';
import { KsefInvoicesService } from 'shared/services/ksef-invoices.service';
import { SnackbarService } from 'shared/services/snackbar.service';
import { FakturniaHighlightComponent } from '@fakturnia/components/highlight';

export interface KsefInvoicePreviewDialogData {
    ksefNumber: string;
    invoiceNumber: string;
}

@Component({
    selector: 'app-ksef-invoice-preview-dialog',
    standalone: true,
    imports: [
        CommonModule,
        MatDialogModule,
        MatButtonModule,
        MatIconModule,
        MatProgressSpinnerModule,
        MatTooltipModule,
        FakturniaHighlightComponent
    ],
    templateUrl: './ksef-invoice-preview-dialog.component.html',
    styleUrls: ['./ksef-invoice-preview-dialog.component.scss']
})
export class KsefInvoicePreviewDialogComponent implements OnInit, OnDestroy {
    private _unsubscribeAll: Subject<void> = new Subject<void>();
    
    public isLoading = true;
    public invoiceXml: string | null = null;
    public error: string | null = null;

    constructor(
        public dialogRef: MatDialogRef<KsefInvoicePreviewDialogComponent>,
        @Inject(MAT_DIALOG_DATA) public data: KsefInvoicePreviewDialogData,
        private _ksefInvoicesService: KsefInvoicesService,
        private _snackbarService: SnackbarService
    ) {}

    /**
     * Getter dla sformatowanego XML z większymi wcięciami
     */
    get formattedXml(): string {
        if (!this.invoiceXml) return '';
        
        try {
            // Parsuj XML i sformatuj z 8 spacjami wcięcia (bardziej widoczne)
            const parser = new DOMParser();
            const xmlDoc = parser.parseFromString(this.invoiceXml, 'text/xml');
            
            // Sprawdź błędy parsowania
            const parserError = xmlDoc.querySelector('parsererror');
            if (parserError) {
                return this.invoiceXml; // Zwróć oryginalny jeśli błąd parsowania
            }
            
            // Sformatuj z wcięciami
            return this.formatXml(xmlDoc);
        } catch (e) {
            console.error('Błąd formatowania XML:', e);
            return this.invoiceXml; // Fallback do oryginalnego
        }
    }

    /**
     * Formatuje XML z czytelnymi wcięciami (8 spacji - bardzo widoczne)
     */
    private formatXml(xml: Document | Element, level: number = 0): string {
        const indent = '        '; // 8 spacji - bardziej widoczne wcięcia
        const padding = indent.repeat(level);
        let formatted = '';

        if (xml instanceof Document) {
            // Deklaracja XML
            formatted += '<?xml version="1.0" encoding="UTF-8"?>\n';
            if (xml.documentElement) {
                formatted += this.formatXml(xml.documentElement, level);
            }
            return formatted;
        }

        const element = xml as Element;
        const tagName = element.tagName;
        const attributes = element.attributes;
        const children = element.childNodes;

        // Otwórz tag
        formatted += padding + '<' + tagName;

        // Atrybuty
        for (let i = 0; i < attributes.length; i++) {
            const attr = attributes[i];
            formatted += ' ' + attr.name + '="' + attr.value + '"';
        }

        // Sprawdź czy ma dzieci
        const hasElementChildren = Array.from(children).some(child => child.nodeType === 1);
        const textContent = element.textContent?.trim() || '';

        if (children.length === 0) {
            // Samozamykający tag
            formatted += '/>\n';
        } else if (!hasElementChildren && textContent) {
            // Tylko tekst
            formatted += '>' + textContent + '</' + tagName + '>\n';
        } else {
            // Ma dzieci
            formatted += '>\n';

            // Dzieci
            for (let i = 0; i < children.length; i++) {
                const child = children[i];
                if (child.nodeType === 1) { // Element node
                    formatted += this.formatXml(child as Element, level + 1);
                }
            }

            // Zamknij tag
            formatted += padding + '</' + tagName + '>\n';
        }

        return formatted;
    }

    ngOnInit(): void {
        this.loadInvoiceFromKsef();
    }

    ngOnDestroy(): void {
        this._unsubscribeAll.next();
        this._unsubscribeAll.complete();
    }

    /**
     * Pobiera fakturę z KSeF
     */
    private async loadInvoiceFromKsef(): Promise<void> {
        this.isLoading = true;
        this.error = null;

        try {
            // Pobierz XML przez nowy serwis (cache w Dexie + backend + MongoDB)
            const xmlContent = await this._ksefInvoicesService.getInvoiceXml(this.data.ksefNumber);
            
            if (xmlContent) {
                this.invoiceXml = xmlContent;
            } else {
                this.error = 'Nie udało się pobrać faktury z KSeF.';
            }
        } catch (error: any) {
            console.error('Błąd podczas pobierania faktury z KSeF:', error);
            
            let errorMessage = 'Nie udało się pobrać faktury z KSeF';
            if (error?.message) {
                errorMessage = error.message;
            }
            
            this.error = errorMessage;
        } finally {
            this.isLoading = false;
        }
    }

    /**
     * Kopiuje XML do schowka
     */
    copyToClipboard(): void {
        if (!this.invoiceXml) return;

        navigator.clipboard.writeText(this.invoiceXml).then(() => {
            this._snackbarService.showSnackBar('XML skopiowany do schowka', 'success', 3000);
        }).catch(() => {
            this._snackbarService.showSnackBar('Błąd kopiowania do schowka', 'error', 3000);
        });
    }

    /**
     * Pobiera XML jako plik
     */
    downloadXml(): void {
        if (!this.invoiceXml) return;

        const blob = new Blob([this.invoiceXml], { type: 'application/xml' });
        const url = window.URL.createObjectURL(blob);
        const link = document.createElement('a');
        link.href = url;
        link.download = `${this.data.invoiceNumber}.xml`;
        link.click();
        window.URL.revokeObjectURL(url);
        
        this._snackbarService.showSnackBar('Pobrano XML faktury', 'success', 3000);
    }

    /**
     * Zamyka dialog
     */
    close(): void {
        this.dialogRef.close();
    }
}

